import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Badge } from 'antd';
import {
  HomeOutlined,
  FileTextOutlined,
  UserOutlined,
  BookOutlined,
  TeamOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/hooks/useAuth';
import { useResponsive } from '@/hooks/useResponsive';

export interface NavItem {
  key: string;
  icon: React.ReactNode;
  label: string;
  path: string;
  badge?: number;
}

/**
 * 移动端底部导航组件
 *
 * 固定在底部，高度 56px，适配安全区域
 * 根据用户角色显示不同的导航项
 */
const MobileBottomNav: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { isMobile } = useResponsive();

  // 只在移动端显示
  if (!isMobile) {
    return null;
  }

  /**
   * 老师移动端导航配置
   */
  const teacherNavItems: NavItem[] = [
    { key: 'dashboard', icon: <HomeOutlined />, label: t('nav.home'), path: '/dashboard' },
    { key: 'classes', icon: <BookOutlined />, label: t('nav.classes'), path: '/classes' },
    { key: 'profile', icon: <UserOutlined />, label: t('nav.mine'), path: '/profile' },
  ];

  /**
   * 管理员移动端导航配置（简化版）
   */
  const adminNavItems: NavItem[] = [
    { key: 'dashboard', icon: <HomeOutlined />, label: t('nav.home'), path: '/dashboard' },
    { key: 'students', icon: <TeamOutlined />, label: t('nav.students'), path: '/students' },
    { key: 'classes', icon: <BookOutlined />, label: t('nav.classes'), path: '/classes' },
    { key: 'profile', icon: <UserOutlined />, label: t('nav.mine'), path: '/profile' },
  ];

  /**
   * 根据角色获取导航配置
   */
  const getNavItemsByRole = (role?: string): NavItem[] => {
    switch (role) {
      case 'teacher':
        return teacherNavItems;
      case 'admin':
        return adminNavItems;
      default:
        return adminNavItems;
    }
  };

  const navItems = getNavItemsByRole(user?.role);
  const activeKey = navItems.find(item =>
    location.pathname === item.path || location.pathname.startsWith(item.path + '/')
  )?.key || 'dashboard';

  const handleNavClick = (path: string) => {
    navigate(path);
  };

  return (
    <nav
      style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        height: 'calc(56px + env(safe-area-inset-bottom, 0px))',
        paddingBottom: 'env(safe-area-inset-bottom, 0px)',
        backgroundColor: '#fff',
        borderTop: '1px solid #f0f0f0',
        display: 'flex',
        justifyContent: 'space-around',
        alignItems: 'center',
        zIndex: 1000,
        boxShadow: '0 -2px 8px rgba(0, 0, 0, 0.06)',
      }}
    >
      {navItems.map((item) => {
        const isActive = item.key === activeKey;
        return (
          <button
            key={item.key}
            onClick={() => handleNavClick(item.path)}
            style={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: '56px',
              border: 'none',
              background: 'none',
              cursor: 'pointer',
              padding: '4px 0',
              transition: 'all 0.2s ease',
              color: isActive ? '#0ea5e9' : '#666',
            }}
          >
            <div style={{ position: 'relative', fontSize: '20px', marginBottom: '2px' }}>
              {item.badge ? (
                <Badge count={item.badge} size="small" offset={[4, -4]}>
                  {item.icon}
                </Badge>
              ) : (
                item.icon
              )}
            </div>
            <span
              style={{
                fontSize: '11px',
                fontWeight: isActive ? 500 : 400,
                transform: isActive ? 'scale(1.05)' : 'scale(1)',
                transition: 'transform 0.2s ease',
              }}
            >
              {item.label}
            </span>
          </button>
        );
      })}
    </nav>
  );
};

export default MobileBottomNav;
